import React, { useEffect } from 'react';

import { MergeConfirmStep } from 'pages/partners/components/MergeTestingCenterWizard/steps/MergeConfirmStep';
import { MergeResultStep } from 'pages/partners/components/MergeTestingCenterWizard/steps/MergeResultStep';
import { MergeReviewStep } from 'pages/partners/components/MergeTestingCenterWizard/steps/MergeReviewStep';
import { MergeSearchStep } from 'pages/partners/components/MergeTestingCenterWizard/steps/MergeSearchStep';
import { GMetrixStepper, StepItem } from 'components/GMetrixStepper/GMetrixStepper';

import 'pages/partners/components/MergeTestingCenterWizard/styles/merge-wizard.scss';

import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Partner, PartnerApi, PartnerDetails } from 'apis/PartnerApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import { useSuperState } from 'hooks';

type MergeTestingCenterWizardProps = {
    isOpen: boolean;
    onClose: () => void;
    onMergeComplete: () => void;
    sourcePartner: PartnerDetails;
};

export const MergeTestingCenterWizard = (props: MergeTestingCenterWizardProps) => {

    const { isOpen, onClose, onMergeComplete, sourcePartner } = props;

    const { formatMessage } = useIntl();

    const STEP_ITEMS: StepItem[] = [
        { label: formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' }) || 'Search' },
        { label: formatMessage({ id: 'review.cea6f6e6', defaultMessage: 'Review' }) || 'Review' },
        { label: formatMessage({ id: 'confirm.297ab8f4', defaultMessage: 'Confirm' }) || 'Confirm' },
        { label: formatMessage({ id: 'result.dfd41abc', defaultMessage: 'Result' }) || 'Result' }
    ];

    const [state, setState, resetState] = useSuperState({
        confirmed: false,
        currentStep: 0,
        error: '',
        loading: false,
        mergeMessage: '',
        mergeSuccess: false,
        selectedTarget: null as PartnerDetails | null,
        targetLoading: false
    });

    useEffect(() => {

        if (!isOpen) {

            resetState();

        }

    }, [isOpen]);

    const handleClose = () => {

        if (state.loading) return;
        onClose();

    };

    const handleMergeCompleteClose = () => {

        onMergeComplete();

    };

    const handleTargetSelect = async (partner: Partner | null) => {

        if (!partner) {

            setState({ selectedTarget: null, targetLoading: false });
            return;

        }

        try {

            setState({ targetLoading: true });
            const details = await PartnerApi.Get(partner.ID);
            setState({ selectedTarget: details, targetLoading: false });

        } catch {

            setState({ selectedTarget: null, targetLoading: false });

        }

    };

    const handleNext = () => {

        if (state.currentStep < 3) {

            setState({ currentStep: state.currentStep + 1 });

        }

    };

    const handleBack = () => {

        if (state.currentStep > 0) {

            setState({
                confirmed: false,
                currentStep: state.currentStep - 1,
                error: ''
            });

        }

    };

    const handleStepperChange = (e: any) => {

        const newStep = e.value;

        // Only allow navigating backward, and not during loading or after result
        if (newStep < state.currentStep && !state.loading && state.currentStep < 3) {

            setState({
                confirmed: false,
                currentStep: newStep,
                error: ''
            });

        }

    };

    const handleExecuteMerge = async () => {

        if (!state.selectedTarget || !state.confirmed) return;

        try {

            setState({ error: '', loading: true });
            const result = await PartnerApi.MergePartners(sourcePartner.ID, state.selectedTarget.ID);

            const hasError = result.Message && (
                result.Message.toLowerCase().includes('conflict') ||
                result.Message.toLowerCase().includes('error') ||
                result.Message.toLowerCase().includes('constraint') ||
                result.Message.toLowerCase().includes('exception') ||
                result.Message.toLowerCase().includes('failed')
            );

            if (result.Done && !hasError) {

                setState({
                    currentStep: 3,
                    loading: false,
                    mergeMessage: result.Message,
                    mergeSuccess: true
                });

            } else {

                setState({
                    currentStep: 3,
                    error: result.Message || formatMessage({ id: 'the_merge_operation_failed.b4268354', defaultMessage: 'The merge operation failed.' }),
                    loading: false,
                    mergeSuccess: false
                });

            }

        } catch (e: any) {

            setState({
                currentStep: 3,
                error: e?.message || formatMessage({ id: 'an_unexpected_error_occurred.5e3346f5', defaultMessage: 'An unexpected error occurred.' }),
                loading: false,
                mergeSuccess: false
            });

        }

    };

    const handleRetry = () => {

        setState({
            confirmed: false,
            currentStep: 2,
            error: '',
            loading: false,
            mergeMessage: '',
            mergeSuccess: false
        });

    };

    const stepItems: StepItem[] = STEP_ITEMS.map((item, index) => ({
        ...item,
        isValid: index < state.currentStep ? true : undefined
    }));

    const isNextDisabled = (() => {

        if (state.currentStep === 0) return !state.selectedTarget || state.targetLoading;
        if (state.currentStep === 2) return !state.confirmed || state.loading;
        return false;

    })();

    const renderStepContent = () => {

        switch (state.currentStep) {

            case 0:
                return (
                    <MergeSearchStep
                        onSelect={handleTargetSelect}
                        selectedTarget={state.selectedTarget}
                        sourcePartner={sourcePartner}
                        targetLoading={state.targetLoading}
                    />
                );

            case 1:
                return state.selectedTarget ? (
                    <MergeReviewStep
                        sourcePartner={sourcePartner}
                        targetPartner={state.selectedTarget}
                    />
                ) : null;

            case 2:
                return state.selectedTarget ? (
                    <MergeConfirmStep
                        confirmed={state.confirmed}
                        error={state.error}
                        loading={state.loading}
                        onConfirmChange={(confirmed: boolean) => setState({ confirmed })}
                        sourcePartner={sourcePartner}
                        targetPartner={state.selectedTarget}
                    />
                ) : null;

            case 3:
                return state.selectedTarget ? (
                    <MergeResultStep
                        error={state.error}
                        message={state.mergeMessage}
                        onClose={state.mergeSuccess ? handleMergeCompleteClose : handleClose}
                        onRetry={handleRetry}
                        sourcePartner={sourcePartner}
                        success={state.mergeSuccess}
                        targetPartner={state.selectedTarget}
                    />
                ) : null;

            default:
                return null;

        }

    };

    const showBackButton = state.currentStep > 0 && state.currentStep < 3;
    const showNextButton = state.currentStep === 0 || state.currentStep === 1;
    const showMergeButton = state.currentStep === 2;

    return (
        <Modal
            scrollable
            backdrop="static"
            keyboard={!state.loading}
            onHide={handleClose}
            show={isOpen}
            size="xl"
        >
            <Modal.Header closeButton={!state.loading}>
                <Modal.Title className="w-100">
                    {<FormattedMessage defaultMessage="Merge Testing Center" description={formatMessage({ id: 'link_name_in_left_menu.c280db16', defaultMessage: 'Link Name in Left Menu' })} id="merge_testing_center.27a5e6d8" /> || 'Merge Testing Center'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="merge-wizard-stepper">
                    <GMetrixStepper
                        handleChange={handleStepperChange}
                        linear={false}
                        setStep={() => { /* handled via handleStepperChange */ }}
                        step={state.currentStep}
                        stepItems={stepItems}
                    />
                </div>
                {renderStepContent()}
            </Modal.Body>

            {state.currentStep < 3 && (
                <Modal.Footer className="border-0 d-flex justify-content-between">
                    <div>
                        {showBackButton && (
                            <GMetrixButton
                                disabled={state.loading}
                                onClick={handleBack}
                                variant="outline-secondary"
                            >
                                <i className="fa-solid fa-arrow-left me-1" />
                                {<FormattedMessage defaultMessage="Back" description="botones" id="back.2bdafbc4" /> || 'Back'}
                            </GMetrixButton>
                        )}
                    </div>
                    <div className="d-flex gap-2">
                        <GMetrixButton
                            disabled={state.loading}
                            onClick={handleClose}
                            variant="outline-secondary"
                        >
                            {<FormattedMessage defaultMessage="Cancel" description="botones" id="cancel.5b254f8e" /> || 'Cancel'}
                        </GMetrixButton>
                        {showNextButton && (
                            <GMetrixButton
                                disabled={isNextDisabled}
                                onClick={handleNext}
                                variant="primary"
                            >
                                {<FormattedMessage defaultMessage="Next" description="botones" id="next.b637640c" /> || 'Next'}
                                <i className="fa-solid fa-arrow-right ms-1" />
                            </GMetrixButton>
                        )}
                        {showMergeButton && (
                            <GMetrixButton
                                disabled={isNextDisabled}
                                disabledTimeout={3000}
                                onClick={handleExecuteMerge}
                                variant="danger"
                            >
                                {state.loading ? (
                                    <>
                                        <i className="fa-solid fa-spinner fa-spin me-1" />
                                        {<FormattedMessage defaultMessage="Merging..." id="merging.2520e800" />}
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-code-merge me-1" />
                                        {<FormattedMessage defaultMessage="Execute Merge" id="execute_merge.9c6e6b40" /> || 'Execute Merge'}
                                    </>
                                )}
                            </GMetrixButton>
                        )}
                    </div>
                </Modal.Footer>
            )}
        </Modal>
    );

};
